import { type AppRole, type OrganizationType } from '@craft/database';

import {
  type AccessType,
  type AccessTypeId,
  AccessTypes,
  hasAccessType,
  type HasAccessType,
  inferAccessTypes,
  type InferredAccessType,
} from './accessTypes';
import { hasEvaluatingAccess } from './authorizers';

/**
 * The name of the flag, {@link AccessTypeFlag<A>}, for the provided access type,
 * {@link AccessType | AccessTypeId}.
 *
 * @example
 * // Typed as 'isSystemAdmin'
 * AccessTypeFlag<AccessTypeId.SystemAdmin>
 */
export type AccessTypeFlag<A extends AccessType | AccessTypeId = AccessTypeId> =
  A extends AccessType ? AccessTypeFlag<A['id']> : A extends AccessTypeId ? `is${A}` : never;

type FalseyAccessTypeKey<R extends AppRole, O extends OrganizationType> = Exclude<
  AccessTypeId,
  InferredAccessType<{ organizationType: O; role: R }>['id']
>;

type TruthyAccessTypeKey<R extends AppRole, O extends OrganizationType> = InferredAccessType<{
  organizationType: O;
  role: R;
}>['id'];

type TruthyFlagsInner<R extends AppRole, O extends OrganizationType> = {
  [key in TruthyAccessTypeKey<R, O> as AccessTypeFlag<key>]: true;
};

type FalseyFlagsInner<R extends AppRole, O extends OrganizationType> = {
  [key in FalseyAccessTypeKey<R, O> as AccessTypeFlag<key>]: false;
};

type AccessTypeFlagsInner<R extends AppRole, O extends OrganizationType> = FalseyFlagsInner<R, O> &
  TruthyFlagsInner<R, O>;

/**
 * Represents an object that contains a role, {@link AppRole}, the organization type,
 * {@link OrganizationType}, the access types associated with that role and organization type,
 * {@link AccessType[]}, along with a series of boolean flags that indicate whether or not the
 * combination of that role, {@link AppRole}, and organization type, {@link OrganizationType}, meets
 * the criteria for a access type {@link AccessType}.
 *
 * Depending on the specifics of the flag, the flags can be used for type narrowing as they ensure
 * mutual exclusiveness for certain conditions.
 *
 * The flags, which represent conditional for the {@link FullRole} type, are broken down into two
 * categories:
 *
 * (1) Role Access Type Flags
 *
 * Flags that correspond directly to only the {@link AppRole}, and not the organization type,
 * {@link OrganizationType}.
 *
 * These flags are mutually exclusive for a given role, {@link AppRole}. In other words, for a given
 * role, {@link AppRole}, only one of these flags will be 'true' at a given time, and the specific
 * flag that is 'true' depends only on the role, {@link AppRole}.
 *
 * - isAdmin
 * - isEvaluator
 * - isInstructor
 * - isLearner
 * - isObserver
 * - isFacilitator
 *
 * (2) Admin Access Type Flags
 *
 * Flags that correspond directly to the {@link OrganizationType}, as long as the underlying role,
 * {@link AppRole}, is {@link AppRole.Admin}.
 *
 * In the case that the underlying role, {@link AppRole}, is {@link AppRole.Admin}, these flags will
 * be mutually exclusive for a given organization type, {@link OrganizationType}.
 *
 * The specific flag that will be 'true' at a given time (assuming the role, {@link AppRole}, is
 * {@link AppRole.Admin}) depends only on the organization type, {@link OrganizationType}.
 *
 * - isEducationProviderAdmin
 * - isEmployerAdmin
 * - isSponsorAdmin
 * - isSystemAdmin
 *
 * @example
 * // Typed as
 * // { isAdmin: true, isLearner: false, isEducationProviderAdmin: true, ... }
 * const flags: AccessTypeFlags<
 *   AppRole.ADMIN,
 *   OrganizationType.EDUCATION_PROVIDER
 * >
 * @example
 * const flags: AccessTypeFlags;
 * if (flags.isAdmin) {
 *   const isEvaluator = flags.isEvaluator; // Typed as 'false';
 * }
 * @example
 * const flags: AccessTypeFlags;
 * if (flags.isSponsorAdmin) {
 *   const isAdmin = flags.isAdmin; // Typed as 'true';
 * }
 * @example
 * const flags: AccessTypeFlags;
 * if (flags.isAdmin) {
 *   const role = flags.role; // Typed as 'ADMIN';
 * }
 */
export type AccessTypeFlags<
  R extends AppRole = AppRole,
  O extends OrganizationType = OrganizationType,
> = R extends AppRole
  ? O extends OrganizationType
    ? {
        readonly accessTypes: InferredAccessType<{ organizationType: O; role: R }>[];
        readonly hasAccessType: <T extends AccessTypeId>(
          accessType: T,
        ) => HasAccessType<{ organizationType: O; role: R }, T>;
        readonly hasAnyAccessType: (accessTypes: AccessTypeId | AccessTypeId[]) => boolean;
        readonly isEvaluatingUser: boolean;
        readonly organizationType: O;
        readonly role: R;
      } & AccessTypeFlagsInner<R, O>
    : never
  : never;

/**
 * Returns the flag, {@link AccessTypeFlag<A>}, for the provided access type, {@link A}.
 */
export const getAccessTypeFlag = <A extends AccessType | AccessTypeId>(
  accessType: A,
): AccessTypeFlag<A> =>
  `is${typeof accessType === 'string' ? accessType : accessType.id}` as AccessTypeFlag<A>;

/**
 * Parses the access type flags object, {@link AccessTypeFlags<R, O>}, for the provided role,
 * {@link R}, and the provided organization type, {@link O}.
 *
 * @param {R} role
 *   The role, {@link AppRole}, that a given user is associated with.
 * @param {O} organizationType
 *   The organization type, {@link OrganizationType}, that a given user is associated with.
 *
 * @returns {AccessTypeFlags<R, O>}
 *   An object containing the boolean access type flags for that role, {@link AppRole}, and
 *   organization type, {@link OrganizationType}, along with the role, {@link AppRole} itself, the
 *   organization type, {@link OrganizationType} itself, and the access types,
 *   {@link InferredAccessType<R, O>[]}.
 */
export const parseAccessTypeFlags = <R extends AppRole, O extends OrganizationType>(
  role: R,
  organizationType: O,
): AccessTypeFlags<R, O> => {
  const positiveFlags = inferAccessTypes({ organizationType, role }).map(
    (accessType): TruthyAccessTypeKey<R, O> => accessType.id as TruthyAccessTypeKey<R, O>,
  );

  const negativeFlags = Object.values(AccessTypes)
    .map(a => a.id)
    .filter(
      (accessTypeId): accessTypeId is FalseyAccessTypeKey<R, O> =>
        !positiveFlags.includes(accessTypeId as TruthyAccessTypeKey<R, O>),
    );

  const flags: AccessTypeFlagsInner<R, O> = {
    ...negativeFlags.reduce(
      (prev, r) => ({ ...prev, [getAccessTypeFlag(r)]: false }),
      {} as FalseyFlagsInner<R, O>,
    ),
    ...positiveFlags.reduce(
      (prev, r) => ({ ...prev, [getAccessTypeFlag(r)]: true }),
      {} as TruthyFlagsInner<R, O>,
    ),
  };
  /* This type coercion is only necessary because of the conditional type on 'AccessTypeFlags' -
     which is necessary, because it forces the type to distribute over the role and organization
     type generics.

     However, since we assigned 'flags' to 'AccessTypeFlagsInner<R, O>' above, without a type
     coercion, we know that the types are compliant, and the type coercion below is 100% safe. */
  return {
    ...flags,
    accessTypes: inferAccessTypes({ organizationType, role }),
    hasAccessType: <T extends AccessTypeId>(
      accessType: T,
    ): HasAccessType<{ organizationType: O; role: R }, T> =>
      hasAccessType(accessType, { organizationType, role }),
    hasAnyAccessType: (accessTypes: AccessTypeId | AccessTypeId[]): boolean =>
      hasAccessType(accessTypes, { organizationType, role }),
    isEvaluatingUser: hasEvaluatingAccess(role),
    organizationType,
    role,
  } as AccessTypeFlags<R, O>;
};
